import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";
import { useForm, Form, Field, getErrors, getInput, validate, type FormStore } from "@formisch/preact";

import { usesCheckboxOptions } from "../checkbox-options.js";
import { buildFormSchema } from "../validation.js";
import { visibleFieldNames } from "../branching.js";
import { FieldRenderer, type AddressPick } from "./fields/index.js";
import { ConsentCheckbox } from "./consent-checkbox.js";
import type { FieldProps } from "./fields/shared.js";
import type { FormEmbedConfig, FormEmbedField } from "../types.js";
import type { AddressVerificationRecordMap, AddressVerificationRecord } from "../address-verification.js";

interface EmbedFormInnerProps {
  embedConfig: FormEmbedConfig;
  submitting: boolean;
  submitError: string;
  onSubmit: (
    values: Record<string, any>,
    fields: FormEmbedField[],
    addressPicks: ReadonlyMap<string, AddressPick>,
    addressVerification?: AddressVerificationRecordMap,
  ) => void;
  /**
   * A submit attempt starts, before validation and address verification.
   * The previous attempt's error no longer describes the form: clear it here,
   * or it stays in place of a validation message or beside a verification
   * prompt that holds this attempt.
   */
  onSubmitAttempt?: () => void;
  /** Embed `prefill` answers; branching conditions can depend on them. */
  prefill?: Record<string, string>;
  /** Web proxy base for address search (`config.embedApiUrl`). */
  addressSearchUrl?: string;

  formClass?: string;
  formId?: string;
  header?: ComponentChildren;
  children: ComponentChildren;
}

function buildInitialValues(fields: FormEmbedField[], hasConsent: boolean): Record<string, any> {
  const initial: Record<string, any> = {};
  for (const field of fields) {
    if (usesCheckboxOptions(field)) {
      initial[field.name] = [];
    } else if (field.type === "checkbox") {
      initial[field.name] = false;
    } else if (field.type === "number") {
      initial[field.name] = field.defaultValue ?? "";
    } else {
      initial[field.name] = "";
    }
  }
  if (hasConsent) {
    initial._consent = false;
  }
  return initial;
}

export function EmbedFormInner({
  embedConfig,
  submitting,
  submitError,
  onSubmit,
  onSubmitAttempt,
  prefill,
  addressSearchUrl,
  formClass,
  formId,
  header,
  children,
}: EmbedFormInnerProps) {
  const fields = embedConfig.screens.map((s) => s.config.field);
  const hasConsent = !!embedConfig.optInConsent;
  const consentRequired = embedConfig.optInConsentRequired === true;
  const branching = !!embedConfig.navigation;
  // Validation runs after the form exists; the ref lets the schema read its raw input.
  const formRef = useRef<FormStore | null>(null);
  const rawInput = () =>
    formRef.current ? (getInput(formRef.current) as Record<string, unknown>) : {};
  const schema = buildFormSchema(
    fields,
    hasConsent && consentRequired,
    branching ? (name) => visibleFieldNames(embedConfig, rawInput(), prefill).has(name) : undefined,
  );

  const form = useForm({
    schema,
    initialInput: buildInitialValues(fields, hasConsent),
  });

  // Anchored on the fields container, not the <form>: formisch's `Form` spreads
  // incoming props and then sets its own `ref`, so a ref passed to it is
  // silently dropped. Every field (consent included) lives inside this wrapper.
  const fieldsRef = useRef<HTMLDivElement>(null);
  // The suggestion picked per address field. Not form values: the answer is
  // the text, and these are its sidecar, sent only while the text still
  // matches the pick.
  const addressPicks = useRef(new Map<string, AddressPick>());
  function onAddressPick(name: string, pick: AddressPick | null) {
    if (pick) addressPicks.current.set(name, pick);
    else addressPicks.current.delete(name);
  }
  // Each address field's auto-selection, run before the form validates — a
  // typed address that a suggestion stands for submits as that pick, like
  // the widget's Continue gate.
  const addressAutoSelects = useRef(new Map<string, () => boolean>());
  const registerAutoSelect = useRef((name: string, autoSelect: (() => boolean) | null) => {
    if (autoSelect) addressAutoSelects.current.set(name, autoSelect);
    else addressAutoSelects.current.delete(name);
  }).current;
  // Each address field's USPS verification, awaited before the submit goes
  // out, and the `_addressVerification` records they publish.
  const addressVerifiers = useRef(new Map<string, () => Promise<boolean>>());
  const registerVerify = useRef((name: string, verify: (() => Promise<boolean>) | null) => {
    if (verify) addressVerifiers.current.set(name, verify);
    else addressVerifiers.current.delete(name);
  }).current;
  const addressRecords = useRef(new Map<string, AddressVerificationRecord>());
  const onAddressRecord = useRef((name: string, record: AddressVerificationRecord | null) => {
    if (record) addressRecords.current.set(name, record);
    else addressRecords.current.delete(name);
  }).current;
  const [verifying, setVerifying] = useState(false);
  const verifyingRef = useRef(false);
  const minimumAddressResolution = embedConfig.minimumAddressResolution;

  /**
   * The widget's Continue gate: verify every address field, one at a time
   * (each can ask for a street number or unit), and send the submit only when
   * all pass. Values are read again afterwards — verification may have added
   * a street number or unit to an address.
   */
  async function submitVerified(output: Record<string, any>) {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    try {
      for (const verify of addressVerifiers.current.values()) {
        if (!(await verify())) return;
      }
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
    const values = { ...output };
    for (const field of visibleFields) {
      if (field.type !== "address") continue;
      const current = getInput(form, { path: [field.name] as any });
      if (typeof current === "string") values[field.name] = current;
    }
    // Only fields still on the form: a field removed in the builder after a
    // held submit, or hidden by branching, must not send its old result.
    const addressNames = new Set(visibleFields.filter((field) => field.type === "address").map((field) => field.name));
    const records = Object.fromEntries([...addressRecords.current].filter(([name]) => addressNames.has(name)));
    onSubmit(values, visibleFields, addressPicks.current, Object.keys(records).length > 0 ? records : undefined);
  }

  // Reading these signals during render subscribes the component, so the
  // message appears and clears as the visitor fixes the fields — and, on a
  // branching form, fields appear and disappear as the answers change.
  formRef.current = form;
  const visible = visibleFieldNames(embedConfig, branching ? rawInput() : {}, prefill);
  const visibleFields = fields.filter((f) => visible.has(f.name));
  const isInvalidAfterSubmit = form.isSubmitted.value && !form.isValid.value;

  /**
   * Bring the first incomplete field into view after a rejected submit.
   *
   * Formisch already focuses it, but a bare `.focus()` scrolls the input to the
   * very top edge of the viewport, so the label above it is clipped and the page
   * barely appears to react to the tap. Measured against the live bundle from a
   * button-in-view scroll position: the page moved 31px and left the field flush
   * at top:0 with its label at -24. Centering puts the label, the input, and the
   * error on screen together.
   *
   * Driven off the submit event rather than the form's `isSubmitting` signal:
   * that signal is set and cleared within one `handleSubmit` call and a signal
   * effect never observes the intermediate `true` (verified against the built
   * bundle). The event also fires on every tap, so a visitor who taps twice gets
   * pulled back to the problem both times instead of only the first.
   *
   * We re-run `validate` rather than reading errors straight away because
   * formisch validates asynchronously — awaiting our own call is deterministic
   * where a fixed timeout would be a race. It is idempotent: it sets the same
   * errors formisch's own pass sets, and does not move focus.
   */
  function revealFirstInvalidField() {
    void validate(form).then((result) => {
      if (result.success) return;

      const names = [...visibleFields.map((f) => f.name), ...(hasConsent ? ["_consent"] : [])];
      const firstInvalid = names.find((name) => getErrors(form, { path: [name] as any }));
      if (!firstInvalid) return;

      // A frame later the error text has been painted, so the field's final
      // height is what we centre against.
      requestAnimationFrame(() => {
        fieldsRef.current
          ?.querySelector<HTMLElement>(`[data-fv-field="${CSS.escape(firstInvalid)}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    });
  }

  return (
    <Form of={form} id={formId} onSubmitCapture={() => {
      // Capture phase: runs before formisch reads and validates the values.
      // Every attempt clears the previous one's error first, including one
      // that validation or address verification then holds.
      onSubmitAttempt?.();
      for (const autoSelect of addressAutoSelects.current.values()) autoSelect();
      revealFirstInvalidField();
    }} onSubmit={(output) => void submitVerified(output as Record<string, any>)} class={formClass ?? "flex flex-col gap-4 px-6 pt-5 pb-6 font-theme"}>
      {header}
      <div ref={fieldsRef} class="flex flex-wrap gap-x-3 gap-y-4">
        {visibleFields.map((field, index) => (
          <Field of={form} path={[field.name] as any} key={field.name}>
            {(fieldStore) => (
              <FieldRenderer
                field={field}
                input={fieldStore.input}
                errors={fieldStore.errors}
                props={fieldStore.props as FieldProps}
                disabled={submitting || verifying}
                form={form}
                isLast={index === visibleFields.length - 1}
                addressSearchUrl={addressSearchUrl}
                onAddressPick={onAddressPick}
                minimumAddressResolution={minimumAddressResolution}
                registerVerify={registerVerify}
                onAddressRecord={onAddressRecord}
                registerAutoSelect={registerAutoSelect}
              />
            )}
          </Field>
        ))}

        {hasConsent && (
          <Field of={form} path={["_consent"] as any} key="_consent">
            {(fieldStore) => (
              <ConsentCheckbox
                text={embedConfig.optInConsent!}
                required={consentRequired}
                input={fieldStore.input}
                errors={fieldStore.errors}
                props={fieldStore.props as FieldProps}
                disabled={submitting || verifying}
                form={form}
              />
            )}
          </Field>
        )}
      </div>

      {/*
        The submit button lives outside this form (each mode renders it in its
        own footer and wires it up with `form=`), so a rejected submit changes
        nothing next to the control the visitor actually tapped. This line sits
        directly above that footer and is the one piece of feedback that is
        guaranteed to be adjacent to the button. `role="alert"` also gives
        screen readers the announcement they were getting nothing of before.
      */}
      <div role="alert" class="empty:hidden text-[13px] text-theme-destructive text-center">
        {submitError || (isInvalidAfterSubmit && "Please complete the highlighted fields above.")}
      </div>

      {children}
    </Form>
  );
}
